export type RgbaColor = {
  r: number;
  g: number;
  b: number;
  a: number;
};

export type VtubeArtMeshColorOverride = {
  id: string;
  multiply: RgbaColor;
  screen: RgbaColor;
};

export type ApplyVtubeArtMeshColorsResult = {
  applied: VtubeArtMeshColorOverride[];
  missing: VtubeArtMeshColorOverride[];
  invalid: Array<{ id?: string; value?: unknown; reason: string }>;
};

export type CubismIdLike =
  | string
  | {
      getString?: () => unknown;
      s?: string;
      _id?: CubismIdLike;
    };

export type CubismDrawableColorTarget = {
  getDrawableCount(): number;
  getDrawableId(drawableIndex: number): CubismIdLike;
  setMultiplyColorByRGBA(
    drawableIndex: number,
    r: number,
    g: number,
    b: number,
    a?: number,
  ): void;
  setScreenColorByRGBA(
    drawableIndex: number,
    r: number,
    g: number,
    b: number,
    a?: number,
  ): void;
  setOverrideFlagForDrawableMultiplyColors(
    drawableIndex: number,
    value: boolean,
  ): void;
  setOverrideFlagForDrawableScreenColors(
    drawableIndex: number,
    value: boolean,
  ): void;
};

type RawVtubeArtMeshColor = {
  ID?: unknown;
  Id?: unknown;
  id?: unknown;
  Value?: unknown;
  value?: unknown;
};

export function applyVtubeArtMeshColors(
  model: CubismDrawableColorTarget,
  vtubeJson: unknown,
): ApplyVtubeArtMeshColorsResult {
  const result = readVtubeArtMeshColorOverrides(vtubeJson);
  const drawableIndexById = createDrawableIndexById(model);

  for (const colorOverride of result.applied.splice(0)) {
    const drawableIndex = drawableIndexById.get(colorOverride.id);
    if (drawableIndex == null) {
      result.missing.push(colorOverride);
      continue;
    }

    model.setMultiplyColorByRGBA(
      drawableIndex,
      colorOverride.multiply.r,
      colorOverride.multiply.g,
      colorOverride.multiply.b,
      colorOverride.multiply.a,
    );
    model.setScreenColorByRGBA(
      drawableIndex,
      colorOverride.screen.r,
      colorOverride.screen.g,
      colorOverride.screen.b,
      colorOverride.screen.a,
    );
    model.setOverrideFlagForDrawableMultiplyColors(drawableIndex, true);
    model.setOverrideFlagForDrawableScreenColors(drawableIndex, true);
    result.applied.push(colorOverride);
  }

  return result;
}

export function readVtubeArtMeshColorOverrides(
  vtubeJson: unknown,
): ApplyVtubeArtMeshColorsResult {
  const result: ApplyVtubeArtMeshColorsResult = {
    applied: [],
    missing: [],
    invalid: [],
  };
  const entries = getRawColorEntries(vtubeJson);

  for (const entry of entries) {
    const id = readRawId(entry);
    const value = readRawValue(entry);

    if (!id) {
      result.invalid.push({ value, reason: "missing artmesh ID" });
      continue;
    }

    if (typeof value !== "string") {
      result.invalid.push({ id, value, reason: "missing color value" });
      continue;
    }

    const colors = parseVtubeColorPair(value);
    if (!colors) {
      result.invalid.push({ id, value, reason: "invalid color pair" });
      continue;
    }

    result.applied.push({ id, multiply: colors.multiply, screen: colors.screen });
  }

  return result;
}

export function parseVtubeColorPair(
  value: string,
): { multiply: RgbaColor; screen: RgbaColor } | null {
  const [multiply, screen] = value.split("|");
  if (multiply == null || screen == null) {
    return null;
  }

  const parsedMultiply = parseHexRgba(multiply);
  const parsedScreen = parseHexRgba(screen);
  if (!parsedMultiply || !parsedScreen) {
    return null;
  }

  return {
    multiply: parsedMultiply,
    screen: parsedScreen,
  };
}

export function parseHexRgba(value: string): RgbaColor | null {
  const hex = value.trim().replace(/^#/, "");
  if (!/^[0-9a-fA-F]{6}([0-9a-fA-F]{2})?$/.test(hex)) {
    return null;
  }

  const withAlpha = hex.length === 6 ? `${hex}ff` : hex;
  return {
    r: Number.parseInt(withAlpha.slice(0, 2), 16) / 255,
    g: Number.parseInt(withAlpha.slice(2, 4), 16) / 255,
    b: Number.parseInt(withAlpha.slice(4, 6), 16) / 255,
    a: Number.parseInt(withAlpha.slice(6, 8), 16) / 255,
  };
}

export function cubismIdToString(value: CubismIdLike | unknown): string {
  if (typeof value === "string") {
    return value;
  }

  if (!value || typeof value !== "object") {
    return "";
  }

  const candidate = value as {
    getString?: () => unknown;
    s?: string;
    _id?: CubismIdLike;
  };

  if (typeof candidate.getString === "function") {
    return cubismIdToString(candidate.getString());
  }

  if (typeof candidate.s === "string") {
    return candidate.s;
  }

  if (candidate._id != null) {
    return cubismIdToString(candidate._id);
  }

  return "";
}

function createDrawableIndexById(
  model: CubismDrawableColorTarget,
): Map<string, number> {
  const indexById = new Map<string, number>();
  const drawableCount = model.getDrawableCount();

  for (let index = 0; index < drawableCount; index += 1) {
    const id = cubismIdToString(model.getDrawableId(index));
    if (id) {
      indexById.set(id, index);
    }
  }

  return indexById;
}

function getRawColorEntries(vtubeJson: unknown): RawVtubeArtMeshColor[] {
  if (!vtubeJson || typeof vtubeJson !== "object") {
    return [];
  }

  const root = vtubeJson as {
    ArtMeshDetails?: {
      ArtMeshMultiplyAndScreenColors?: unknown;
    };
  };
  const entries = root.ArtMeshDetails?.ArtMeshMultiplyAndScreenColors;

  return Array.isArray(entries) ? (entries as RawVtubeArtMeshColor[]) : [];
}

function readRawId(entry: RawVtubeArtMeshColor): string {
  const id = entry.ID ?? entry.Id ?? entry.id;
  return typeof id === "string" ? id : "";
}

function readRawValue(entry: RawVtubeArtMeshColor): unknown {
  return entry.Value ?? entry.value;
}
